import PropTypes from 'prop-types';
import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CircularProgress } from '@material-ui/core';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import TourItinerary from '../TourItinerary';
import TourBookingColumn from '../TourBookingColumn';
import { toggleMapVisibility } from '../../../../store/slices/tripSearch';
import { SearchStatus } from '../../../../constants';

const TourList = ({
  tripId,
  isInsideModal,
  isModalOpen,
  tourToScroll,
  onViewMoreClick,
  onModalClose,
}) => {
  const dispatch = useDispatch();
  const { responses, status, error } = useSelector(
    (state) => state.tour.tourSearches[tripId] || {},
  );
  const { isMapShown } = useSelector((state) => state.tripSearch);
  const [openProductId, setOpenProductId] = useState(null);
  const doRestoreMap = useRef(false);

  useEffect(() => {
    if (isModalOpen) {
      setOpenProductId(null);
    }
  }, [isModalOpen]);

  useEffect(() => {
    if (openProductId && isMapShown) {
      doRestoreMap.current = true;
      dispatch(toggleMapVisibility(false));
    }
    if (!openProductId && doRestoreMap.current) {
      doRestoreMap.current = false;
      dispatch(toggleMapVisibility(true));
    }
  }, [openProductId, isMapShown, dispatch]);
  const travelOptions = (responses && responses.travelOptions) || [];
  const { errorMessage } = responses || {};
  const isSearching = status === SearchStatus.SEARCHING;
  const isDone = status === SearchStatus.DONE;
  const isFailed = status === SearchStatus.ERROR;

  return (
    <>
      {isSearching && (
        <div className="text-center">
          <CircularProgress color="secondary" />
        </div>
      )}
      {isDone &&
        travelOptions.map((travelOption) => {
          const { itinerary } = travelOption;
          const isBookingOpen = openProductId === itinerary.productId;

          return (
            <div
              key={itinerary?.uniqueKey}
              className={clsx('grid grid-cols-1', {
                'md:grid-cols-3 xl:grid-cols-4': isBookingOpen,
              })}
            >
              <div className={clsx('min-w-0', { 'md:col-span-2 xl:col-span-3': isBookingOpen })}>
                <TourItinerary
                  itinerary={itinerary}
                  doShowAvailabilityButton
                  isBookingOpen={isBookingOpen}
                  isExpanded={{ value: tourToScroll === itinerary.productId }}
                  onToggleBooking={() =>
                    setOpenProductId(isBookingOpen ? null : itinerary.productId)
                  }
                />
              </div>
              {isBookingOpen && (
                <TourBookingColumn
                  itinerary={itinerary}
                  tripId={tripId}
                  onModalClose={onModalClose}
                />
              )}
            </div>
          );
        })}
      {isDone && travelOptions.length > 0 && (
        <button
          onClick={onViewMoreClick}
          type="button"
          className="border border-theme block rounded-pill text-center rounded-2xl text-x text-theme py-3 mx-auto mb-5 w-64 font-bold mt-2"
        >
          {isInsideModal ? (
            <span>
              View less
              <FaChevronUp className="inline-block ml-3" />
            </span>
          ) : (
            <span>
              View more
              <FaChevronDown className="inline-block ml-3" />
            </span>
          )}
        </button>
      )}
      {isDone && travelOptions.length === 0 && (
        <div className="text-center py-4">{errorMessage || 'No tours found for your input.'}</div>
      )}
      {isFailed && (
        <div className="text-center py-4">
          <div>Tours could not be loaded. Please try the search again.</div>
          {error && <div className="text-sm text-grey-500 mt-1">{error}</div>}
        </div>
      )}
    </>
  );
};

TourList.propTypes = {
  tripId: PropTypes.string,
  isInsideModal: PropTypes.bool,
  isModalOpen: PropTypes.bool,
  tourToScroll: PropTypes.string,
  onViewMoreClick: PropTypes.func.isRequired,
  onModalClose: PropTypes.func,
};

TourList.defaultProps = {
  tripId: undefined,
  isInsideModal: false,
  isModalOpen: false,
  tourToScroll: undefined,
  onModalClose: undefined,
};

export default TourList;
